import * as React from "react";

import { ORDER_GENERATED_BY, ORDER_STATUS, ORDER_TYPE } from "@/lib/ManufacturingConsts.generated";
import { ROLE_ADMIN, isInRole } from "@/lib/auth";
import { getBackend } from "@/lib/backend/backend2";
import { niceNumber } from "@/lib/util";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import { getPlantsForUser } from "@/lib/businessLogic";
import OrderTableProductionModal from "@/modules/shared/orderProduction/OrderTableProductionModal";
import LargeDataTable, {
    ColumnId,
    FilterParser,
    booleanToYesNo,
    getFieldNameMax,
    getFieldNameMin,
    formatBoolean,
    formatDate,
    formatDateTime,
    formatMaterialTitle,
    DateRangeFilter,
    TextFilter,
    MultiSelectFilter,
    SelectFilter,
    BooleanCell,
    FloatCell,
    DateCell,
    DateTimeCell,
    MaterialTitleCell,
    TextFilterWithReset,
    DefaultFiltersForm,
    DEFAULT_PAGE_SIZE,
} from "@/modules/shared/largeDataTable/LargeDataTable";

import type { IOrderProduced, IOrderModelBase, IFilterOrdersReq } from "@/lib/backend/manufacturing2.generated.types";
import type {
    Filters,
    TableColumn,
    TableCellProps,
    TableExportData,
} from "@/modules/shared/largeDataTable/LargeDataTable";

type TableRow = {
    capacity_factor: number;
    comment: string;
    created_at: number;
    earliest_end: number;
    earliest_start: number;
    external_id: string;
    external_operation_id: string;
    generated_by: string;
    initial_earliest_end: number;
    initial_earliest_start: number;
    line_external_id: string;
    line_title: string;
    material_external_id: string;
    material_title: string;
    norm_base_units: number;
    norm_break_time_hours: number;
    norm_clearing_time: number;
    norm_machine_time: number;
    norm_person_time: number;
    norm_tool_setup_time: number;
    order_type: string;
    output_stock: boolean;
    override_capacity_factor?: number;
    plant_external_id: string;
    plant_title: string;
    plant_uuid: string;
    process_num: string;
    process_uuid: string;
    produced_available_in_hours: number;
    quantity_produced: number;
    quantity_scrap: number;
    quantity_total: number;
    sequence_weight: number;
    sim_ignore_earliest_start?: boolean;
    skip_sim: boolean;
    status: string;
    time_end?: number;
    time_start?: number;
    title: string;
    uuid: string;
    wait_time_hours: number;
};

type Props = {};

type State = {
    default_filters: Filters<TableRow> | null;
    error: string;
    is_loading: boolean;
    is_updating: boolean;
    total_count: number;
    rows: TableRow[];
    selected_row: TableRow | null;
    show_details: boolean;
    show_order_modal: boolean;
    is_scrolled_x: boolean;
};

const DATA_TABLE_ID = "production-data-operations";

const getDefaultFilters = (start_date: Date): Filters<TableRow> => {
    return {
        order_type: Object.keys(ORDER_TYPE).join(","),
        status: Object.keys(ORDER_STATUS).join(","),
        [getFieldNameMin("time_start")]: start_date.toISOString(),
    } as Filters<TableRow>;
};

const getFiterRequestParams = (
    filters: Filters<TableRow>,
    page_size?: number,
    page_index?: number,
    order_by?: ColumnId<TableRow>,
    order_desc?: boolean,
    count_filtered?: boolean
): IFilterOrdersReq => {
    const filter_parser = new FilterParser(filters);
    const filter_params: IFilterOrdersReq = {
        plant_uuids: getPlantsForUser().map(plant => plant.uuid),
        order_type: filter_parser.getOptions("order_type"),
        status: filter_parser.getOptions("status"),
        external_id: filter_parser.get("external_id"),
        external_operation_id: filter_parser.get("external_operation_id"),
        title: filter_parser.get("title"),
        process_num: filter_parser.get("process_num"),
        material_external_id: filter_parser.get("material_external_id"),
        material_title: filter_parser.get("material_title"),
        line_external_id: filter_parser.get("line_external_id"),
        line_title: filter_parser.get("line_title"),
        plant_external_id: filter_parser.get("plant_external_id"),
        plant_title: filter_parser.get("plant_title"),
        time_start_min: filter_parser.getTimestamp(getFieldNameMin("time_start")),
        time_start_max: filter_parser.getTimestamp(getFieldNameMax("time_start")),
        time_end_min: filter_parser.getTimestamp(getFieldNameMin("time_end")),
        time_end_max: filter_parser.getTimestamp(getFieldNameMax("time_end")),
        earliest_start_min: filter_parser.getTimestamp(getFieldNameMin("earliest_start")),
        earliest_start_max: filter_parser.getTimestamp(getFieldNameMax("earliest_start")),
        earliest_end_min: filter_parser.getTimestamp(getFieldNameMin("earliest_end")),
        earliest_end_max: filter_parser.getTimestamp(getFieldNameMax("earliest_end")),
        output_stock: filter_parser.getBoolean("output_stock"),
        sim_ignore_earliest_start: filter_parser.getBoolean("sim_ignore_earliest_start"),
        skip_sim: filter_parser.getBoolean("skip_sim"),
        limit: page_size,
        offset: page_size !== undefined && page_index !== undefined ? page_index * page_size : undefined,
        order_by,
        order_desc,
        count_filtered,
    };

    return filter_params;
};

const mapRow = (order: IOrderModelBase, order_produced?: IOrderProduced): TableRow => {
    return {
        capacity_factor: order.capacity_factor,
        comment: order.order_comment || "",
        created_at: order.created_at,
        earliest_end: order.earliest_end,
        earliest_start: order.earliest_start,
        external_id: order.external_id,
        external_operation_id: order.external_operation_id,
        generated_by: order.generated_by,
        initial_earliest_end: order.initial_earliest_end,
        initial_earliest_start: order.initial_earliest_start,
        line_external_id: order.line.external_id,
        line_title: order.line.title,
        material_external_id: order.material_external_id,
        material_title: order.material_title,
        norm_base_units: order.norm_base_units,
        norm_break_time_hours: order.norm_break_time_hours,
        norm_clearing_time: order.norm_clearing_time,
        norm_machine_time: order.norm_machine_time,
        norm_person_time: order.norm_person_time,
        norm_tool_setup_time: order.norm_tool_setup_time,
        order_type: order.order_type,
        output_stock: order.output_stock,
        override_capacity_factor: order.override_capacity_factor,
        plant_external_id: order.line.plant_external_id,
        plant_title: order.line.plant_title,
        plant_uuid: order.line.plant_uuid,
        process_num: order.process_num,
        process_uuid: order.process_uuid,
        produced_available_in_hours: order.produced_available_in_hours,
        quantity_produced: order_produced ? order_produced.quantity_produced : 0,
        quantity_scrap: order_produced ? order_produced.quantity_scrap : 0,
        quantity_total: order.quantity_total,
        sequence_weight: order.sequence_weight,
        sim_ignore_earliest_start: order.sim_ignore_earliest_start,
        skip_sim: order.skip_sim,
        status: order.status,
        time_end: order.time_end,
        time_start: order.time_start,
        title: order.title,
        uuid: order.uuid,
        wait_time_hours: order.wait_time_hours,
    };
};

const formatOutputToStock = (props: TableCellProps<TableRow>): string => {
    const { row } = props;
    const available_in_hours =
        row.produced_available_in_hours > 0 ? ` (+${niceNumber(row.produced_available_in_hours, 2)}h)` : "";
    return `${formatBoolean(props)}${available_in_hours}`;
};

const formatSkipSim = (props: TableCellProps<TableRow>): string => {
    const { row, table_props } = props;
    const value = row.skip_sim;
    return booleanToYesNo(!table_props.show_details ? !value : value);
};

const formatStatus = (props: TableCellProps<TableRow>): string => {
    const { row } = props;
    const value = row.status;
    return translate(`order_status.${value}`, `${value}`);
};

const formatOrderType = (props: TableCellProps<TableRow>): string => {
    const { row } = props;
    const value = row.order_type;
    return translate(`order_type.${value}`, `${value}`);
};

const formatQuantity = (props: TableCellProps<TableRow>): number | null => {
    const { column, row } = props;
    if (row.generated_by === ORDER_GENERATED_BY.manual_entry_subcontractor) {
        return null;
    }

    return Number(row[column.id]);
};

const OutputToStockCell = (props: TableCellProps<TableRow>) => {
    return <span>{formatOutputToStock(props)}</span>;
};

const StatusCell = (props: TableCellProps<TableRow>) => {
    return <span>{formatStatus(props)}</span>;
};

const OrderTypeCell = (props: TableCellProps<TableRow>) => {
    return <span>{formatOrderType(props)}</span>;
};

const SkipSimCell = (props: TableCellProps<TableRow>) => {
    return <span>{formatSkipSim(props)}</span>;
};

const QuantityCell = (props: TableCellProps<TableRow>) => {
    const { row } = props;
    if (row.generated_by === ORDER_GENERATED_BY.manual_entry_subcontractor) {
        return <></>;
    }

    return <FloatCell {...props} />;
};

const ExternalIdCell = (
    props: TableCellProps<TableRow> & { onShowOrderModal: (row: TableRow) => void }
): React.ReactElement => {
    const { row, onShowOrderModal } = props;
    const value = row.external_id;
    return (
        <div className="large-data-table__external-id">
            <button
                className="btn btn-short btn-outline-secondary fas fa-eye mr-2"
                title={translate("common.tooltip_show_details", "Show details")}
                onClick={() => onShowOrderModal(row)}
                disabled={row.order_type === ORDER_TYPE.chunk}
            />
            <span>{value}</span> <b className="text-muted"> {row.process_num}</b>
        </div>
    );
};

class Operations extends React.Component<Props, State> {
    state: State = {
        default_filters: null,
        error: "",
        is_loading: false,
        is_updating: false,
        total_count: -1,
        rows: [],
        selected_row: null,
        show_details: false,
        show_order_modal: false,
        is_scrolled_x: false,
    };

    loadData = async (
        filters: Filters<TableRow>,
        page_index: number,
        page_size: number,
        order_by?: ColumnId<TableRow>,
        order_desc?: boolean,
        is_updating = false
    ) => {
        this.setState({
            is_loading: !is_updating,
            is_updating,
        });
        try {
            let order_by_column = order_by;
            if (order_by === "comment") {
                order_by_column = `tags.${order_by}` as ColumnId<TableRow>;
            }

            const { orders, count } = await getBackend().manufacturing.filterOrders(
                getFiterRequestParams(filters, page_size, page_index, order_by_column, order_desc)
            );
            const total_count = count !== undefined ? count : this.state.total_count;
            const page_orders: IOrderModelBase[] =
                page_index < 0 && count !== undefined
                    ? orders.slice(Math.floor(count / page_size) * page_size - count)
                    : orders;
            const { orders_produced } = await getBackend().manufacturing.getOrdersProduced({
                order_uuids: page_orders.map(order => order.uuid),
            });
            const orders_produced_map: Map<string, IOrderProduced> = new Map(
                orders_produced.map(order => [order.order_uuid, order])
            );

            const rows: TableRow[] = page_orders.map(order => {
                const order_produced = orders_produced_map.get(order.uuid);
                return mapRow(order, order_produced);
            });

            this.setState({
                rows,
                total_count,
            });
        } catch (error) {
            this.setState({ error: (error as Error).message });
        }

        this.setState({
            is_loading: false,
            is_updating: false,
        });
    };

    loadExportData = async (
        export_all: boolean,
        filters: Filters<TableRow>,
        order_by: ColumnId<TableRow>,
        order_desc: boolean
    ): Promise<TableExportData<TableRow>> => {
        let rows: TableRow[] | null = this.state.rows;
        if (export_all) {
            try {
                let order_by_column = order_by;
                if (order_by === "comment") {
                    order_by_column = `tags.${order_by}` as ColumnId<TableRow>;
                }

                const { orders } = await getBackend().manufacturing.filterOrders(
                    getFiterRequestParams(filters, undefined, undefined, order_by_column, order_desc)
                );
                const { orders_produced } = await getBackend().manufacturing.getOrdersProduced({
                    order_uuids: orders.map(order => order.uuid),
                });
                const orders_produced_map: Map<string, IOrderProduced> = new Map(
                    orders_produced.map(order => [order.order_uuid, order])
                );
                rows = orders.map(order => {
                    const order_produced = orders_produced_map.get(order.uuid);
                    return mapRow(order, order_produced);
                });
            } catch {
                rows = null;
            }
        }

        return {
            rows,
            title: translate("Header.menu.operations", "Operations"),
            file_name: "operations",
        };
    };

    getColumns = (): TableColumn<TableRow>[] => {
        const { show_details } = this.state;
        const columns: TableColumn<TableRow>[] = [
            {
                id: "external_id",
                title: translate("OrderTableProduction.order_id", "Order ID"),
                Header: TextFilterWithReset,
                Cell: props => <ExternalIdCell {...props} onShowOrderModal={this.handleOrderModalShow} />,
            },
            {
                id: "process_num",
                title: translate("OrderTableProduction.process_num", "Process number"),
                Header: TextFilter,
            },
            {
                id: "external_operation_id",
                title: translate("OrderTableProduction.external_operation_id", "Operation ID"),
                Header: TextFilter,
            },
            {
                id: "title",
                title: translate("common.operation", "Operation"),
                Header: TextFilter,
            },
            {
                id: "material_external_id",
                title: translate("OrderTableProduction.material_external_id", "Material ID"),
                Header: TextFilter,
            },
            {
                id: "material_title",
                title: translate("common.material", "Material"),
                Header: TextFilter,
                Cell: MaterialTitleCell,
                formatExport: formatMaterialTitle,
            },
            {
                id: "line_external_id",
                title: translate("OrderTableProduction.line_external_id", "Line ID"),
                Header: TextFilter,
            },
            {
                id: "line_title",
                title: translate("common.line", "Line"),
                Header: TextFilter,
            },
            {
                id: "plant_external_id",
                title: translate("common.plant_id", "Plant ID"),
                Header: TextFilter,
            },
            {
                id: "plant_title",
                title: translate("common.plant", "Plant"),
                Header: TextFilter,
            },
            {
                id: "time_start",
                title: translate("OrderTableProduction.time_start", "Start time"),
                Header: DateRangeFilter,
                Cell: DateTimeCell,
                formatExport: formatDateTime,
            },
            {
                id: "time_end",
                title: translate("OrderTableProduction.time_end", "End time"),
                Header: DateRangeFilter,
                Cell: DateTimeCell,
                formatExport: formatDateTime,
            },
            {
                id: "earliest_start",
                title: translate("common.earliest_start", "ERP start"),
                Header: DateRangeFilter,
                Cell: DateCell,
                formatExport: formatDate,
            },
            {
                id: "earliest_end",
                title: translate("common.earliest_end", "ERP end"),
                Header: DateRangeFilter,
                Cell: DateCell,
                formatExport: formatDate,
            },
            {
                id: "status",
                title: translate("OrderTableProduction.status", "Operation status"),
                Header: MultiSelectFilter,
                Cell: StatusCell,
                formatExport: formatStatus,
                disable_sort: true,
                filter_options: Object.keys(ORDER_STATUS).map(value => ({
                    value,
                    label: translate(`order_status.${value}`, `${value}`),
                })),
            },
            {
                id: "order_type",
                title: translate("OrderTableProduction.order_type", "Order type"),
                Header: MultiSelectFilter,
                Cell: OrderTypeCell,
                formatExport: formatOrderType,
                disable_sort: true,
                filter_options: Object.keys(ORDER_TYPE).map(value => ({
                    value,
                    label: translate(`order_type.${value}`, `${value}`),
                })),
            },
            {
                id: "quantity_scrap",
                title: translate("common.scrap", "Scrap"),
                Cell: QuantityCell,
                formatExport: formatQuantity,
            },
            {
                id: "quantity_produced",
                title: translate("OrderTableProduction.quantity_produced", "Quantity produced"),
                Cell: QuantityCell,
                formatExport: formatQuantity,
            },
            {
                id: "quantity_total",
                title: translate("OrderTableProduction.quantity_total", "Operation total quantity"),
                Cell: FloatCell,
            },
            {
                id: "output_stock",
                title: translate("OrderTableProduction.output_stock", "Outputs to stock"),
                Header: SelectFilter,
                Cell: OutputToStockCell,
                formatExport: formatOutputToStock,
                disable_sort: true,
                filter_options: [
                    { value: String(false), label: translate("common.no", "No") },
                    { value: String(true), label: translate("common.yes", "Yes") },
                ],
            },
            {
                id: "norm_base_units",
                title: translate("OrderTableProduction.norm_base_units", "Norm base"),
                Cell: FloatCell,
            },
            {
                id: "norm_machine_time",
                title: translate("OrderTableProduction.norm_machine_time", "Norm (machine)"),
                Cell: FloatCell,
            },
            {
                id: "norm_person_time",
                title: translate("OrderTableProduction.norm_person_time", "Norm (person)"),
                Cell: FloatCell,
            },
            {
                id: "norm_break_time_hours",
                title: translate("OrderTableProduction.norm_break_time_hours", "Break time"),
                Cell: FloatCell,
            },
            {
                id: "norm_clearing_time",
                title: translate("OrderTableProduction.norm_clearing_time", "Clearing time"),
                Cell: FloatCell,
            },
            {
                id: "norm_tool_setup_time",
                title: translate("OrderTableProduction.norm_tool_setup_time", "Tool setup time"),
                Cell: FloatCell,
            },
            {
                id: "created_at",
                title: translate("OrderTableProduction.created_at", "Created at"),
                Cell: DateTimeCell,
                formatExport: formatDateTime,
            },
            {
                id: "capacity_factor",
                title: translate("OrderTableProduction.capacity_factor", "Capacity factor"),
                Cell: FloatCell,
            },
            {
                id: "initial_earliest_start",
                title: translate("OrderTableProduction.initial_earliest_start", "Initial earliest start time"),
                Cell: DateCell,
                formatExport: formatDate,
            },
            {
                id: "initial_earliest_end",
                title: translate("OrderTableProduction.initial_earliest_end", "Initial earliest end time"),
                Cell: DateCell,
                formatExport: formatDate,
            },
            {
                id: "override_capacity_factor",
                title: translate("OrderTableProduction.override_capacity_factor", "Override capacity factor"),
                Cell: FloatCell,
            },
            {
                id: "wait_time_hours",
                title: translate("OrderTableProduction.wait_time_hours", "Interoperation time"),
                Cell: FloatCell,
            },
            {
                id: "produced_available_in_hours",
                title: translate("OrderTableProduction.produced_available_in_hours", "Delay to available on stock"),
                Cell: FloatCell,
            },
            {
                id: "sequence_weight",
                title: translate("OrderTableProduction.sequence_weight", "Sequence weight"),
                Cell: FloatCell,
            },
            {
                id: "sim_ignore_earliest_start",
                title: translate("OrderTableProduction.sim_ignore_earliest_start", "Ignore earliest start"),
                Header: SelectFilter,
                Cell: BooleanCell,
                formatExport: formatBoolean,
                disable_sort: true,
                filter_options: [
                    { value: String(false), label: translate("common.no", "No") },
                    { value: String(true), label: translate("common.yes", "Yes") },
                ],
            },
            {
                id: "skip_sim",
                title: show_details
                    ? translate("OrderTableProduction.skip_sim", "Skip simulation")
                    : translate("common.scheduled_operation_abbr", "Scheduled operation"),
                Header: SelectFilter,
                Cell: SkipSimCell,
                formatExport: formatSkipSim,
                disable_sort: true,
                filter_options: [
                    {
                        value: String(false),
                        label: !show_details ? translate("common.yes", "Yes") : translate("common.no", "No"),
                    },
                    {
                        value: String(true),
                        label: !show_details ? translate("common.no", "No") : translate("common.yes", "Yes"),
                    },
                ],
            },
            {
                id: "uuid",
                title: translate("OrderTableProduction.uuid", "UUID"),
            },
            {
                id: "plant_uuid",
                title: translate("OrderTableProduction.plant_uuid", "Plant UUID"),
            },
            {
                id: "process_uuid",
                title: translate("OrderTableProduction.process_uuid", "Process UUID"),
            },
            {
                id: "comment",
                title: translate("common.comment", "Comment"),
            },
        ];

        const details_columns = new Set<ColumnId<TableRow>>([
            "capacity_factor",
            "created_at",
            "initial_earliest_end",
            "initial_earliest_start",
            "norm_break_time_hours",
            "norm_clearing_time",
            "norm_machine_time",
            "norm_person_time",
            "norm_tool_setup_time",
            "override_capacity_factor",
            "plant_uuid",
            "process_uuid",
            "produced_available_in_hours",
            "sequence_weight",
            "sim_ignore_earliest_start",
            "uuid",
            "wait_time_hours",
        ]);

        return columns.filter(column => this.state.show_details || !details_columns.has(column.id));
    };

    handleDataUpdate = async (
        filters: Filters<TableRow>,
        page_index: number,
        page_size: number,
        order_by: ColumnId<TableRow>,
        order_desc: boolean
    ) => {
        await this.loadData(filters, page_index, page_size, order_by, order_desc, true);
    };

    handleTotalCountUpdate = async (filters: Filters<TableRow>) => {
        const { count } = await getBackend().manufacturing.filterOrders(
            getFiterRequestParams(filters, 0, 0, undefined, false, true)
        );

        this.setState({ total_count: count ?? 0 });
    };

    handleFiltersChange = () => {
        this.setState({ total_count: -1 });
    };

    handleOrderSave = (order: IOrderModelBase) => {
        const row = this.state.rows.find(row => row.uuid === order.uuid);
        if (row !== undefined) {
            row.comment = order.order_comment || "";
        }
    };

    handleOrderModalShow = (row: TableRow) => {
        this.setState({
            show_order_modal: true,
            selected_row: row,
        });
    };

    handleOrderModalHide = () => {
        this.setState({ show_order_modal: false });
    };

    handleDetailsToggle = (show: boolean) => {
        this.setState({ show_details: show });
    };

    handleScroll = (event: React.UIEvent<HTMLElement>) => {
        if (event.currentTarget.scrollLeft > 50) {
            if (!this.state.is_scrolled_x) {
                this.setState({ is_scrolled_x: true });
            }
        } else if (this.state.is_scrolled_x) {
            this.setState({ is_scrolled_x: false });
        }
    };

    handleSubmit = async (filters: Filters<TableRow>) => {
        this.setState({ default_filters: filters });
        await this.loadData(filters, 0, DEFAULT_PAGE_SIZE);
    };

    render() {
        const {
            default_filters,
            error,
            is_loading,
            is_updating,
            is_scrolled_x,
            rows,
            selected_row,
            show_details,
            show_order_modal,
            total_count,
        } = this.state;
        const is_admin = isInRole(ROLE_ADMIN);

        if (default_filters === null) {
            return (
                <DefaultFiltersForm
                    table_id={DATA_TABLE_ID}
                    title={translate("common.search_operations", "Search operations")}
                    getDefaultFilters={getDefaultFilters}
                    onSubmit={this.handleSubmit}
                />
            );
        }

        return (
            <>
                <LargeDataTable
                    id={DATA_TABLE_ID}
                    class_name={is_scrolled_x ? "is-scrolled-x" : ""}
                    error={error}
                    is_loading={is_loading}
                    is_updating={is_updating}
                    show_details={is_admin ? show_details : undefined}
                    total_count={total_count}
                    default_page_size={DEFAULT_PAGE_SIZE}
                    default_filters={default_filters}
                    columns={this.getColumns()}
                    rows={rows}
                    always_visible_columns={["external_id", "process_num", "material_external_id"]}
                    loadExportData={this.loadExportData}
                    onDataUpdate={this.handleDataUpdate}
                    onTotalCountUpdate={this.handleTotalCountUpdate}
                    onDetailsToggle={is_admin ? this.handleDetailsToggle : undefined}
                    onFiltersChange={this.handleFiltersChange}
                    onScroll={this.handleScroll}
                />
                {selected_row !== null && (
                    <OrderTableProductionModal
                        show={show_order_modal}
                        selected_order_uuid={selected_row.uuid}
                        selected_order_external_id={selected_row.external_id}
                        process_num={selected_row.process_num}
                        onShowOrHideOrderModal={this.handleOrderModalHide}
                        onOrderSave={this.handleOrderSave}
                        override_description_fields={["external_id", "material_external_id", "material_title"]}
                        show_columns={[
                            "process_num",
                            "title",
                            "quantity_produced",
                            "quantity_scrap",
                            "quantity_total",
                            "output_stock",
                            "status",
                            "time_start",
                            "time_end",
                            "skip_sim",
                            "ignore_on_planning_board",
                        ]}
                    />
                )}
            </>
        );
    }
}

export default Operations;
